import {useEffect, useRef} from "react";
import {Outlet, useNavigate} from "react-router-dom";
import {getClient} from "@sakura-grp/database";
import {useAuthStore} from "@/stores/auth";
import {useHostessStore} from "@/stores/hostess";

/**
 * 認証済みホステス専用のレイアウト
 * - 認証完了まで子ルートをブロック
 * - ホステス権限チェックを一元化
 * - ローディング/エラー状態を統一表示
 */
export function ProtectedHostessLayout() {
  const navigate = useNavigate();
  const supabase = getClient();

  // 認証状態
  const {session, user, loading: authLoading} = useAuthStore();

  // ホステス状態
  const {hostess, loading: hostessLoading, setHostess, setLoading, clear} = useHostessStore();

  // 重複取得防止フラグ
  const fetchAttempted = useRef(false);
  // リダイレクト中フラグ（signOut後の二重リダイレクト防止）
  const redirecting = useRef(false);

  useEffect(() => {
    async function checkHostessPermission() {
      // リダイレクト処理中は何もしない
      if (redirecting.current) {
        return;
      }

      // 既にホステス情報がある場合はスキップ
      if (hostess) {
        console.log("[ProtectedHostessLayout] ホステス情報取得済み:", hostess.name);
        return;
      }

      // 同じセッションで既に取得を試みた場合はスキップ
      if (fetchAttempted.current) {
        return;
      }

      // 認証チェックがまだ完了していない場合は待機
      if (authLoading) {
        return;
      }

      // 未認証の場合、サインインページへリダイレクト
      if (!session || !user) {
        console.log("[ProtectedHostessLayout] 未認証 - サインインページへリダイレクト");
        setLoading(false);
        navigate("/sign-in", {replace: true});
        return;
      }

      // 取得試行フラグを立てる
      fetchAttempted.current = true;

      // hostesses を user_id で検索
      const fetchHostessByUserId = async () => {
        return await supabase
          .from("hostesses")
          .select(`
            *,
            current_assignment:hostess_store_assignments(
              store_id,
              assigned_from,
              store:stores(id, store_name, logo_path)
            )
          `)
          .eq("user_id", user.id)
          .eq("status", "active")
          .is("deleted_at", null)
          .is("current_assignment.assigned_to", null)
          .single();
      };

      try {
        console.log("[ProtectedHostessLayout] hostessesテーブル検索開始:", {
          userId: user.id,
        });

        let {data, error: fetchError} = await fetchHostessByUserId();

        console.log("[ProtectedHostessLayout] クエリ結果:", {
          hasData: !!data,
          hasError: !!fetchError,
          errorCode: fetchError?.code,
        });

        if (fetchError && fetchError.code !== "PGRST116") {
          // PGRST116以外のエラーは予期しないエラー
          console.error("[ProtectedHostessLayout] 予期しないエラー:", fetchError);
          setLoading(false);
          return;
        }

        if (!data) {
          // 救済: email 一致で user_id を紐付けられる未紐付け hostess があるか試す
          // 背景: bulk import で user_id が空の hostesses に対し、auth.users が
          //       既に存在するケース (INSERT トリガーが効かない) を救済する。
          console.log("[ProtectedHostessLayout] user_id 一致なし - claim_hostess_for_current_user で救済を試行");
          const {data: claimedId, error: claimError} = await supabase.rpc("claim_hostess_for_current_user");

          if (claimError) {
            console.error("[ProtectedHostessLayout] claim RPC エラー:", claimError);
          } else if (claimedId) {
            console.log("[ProtectedHostessLayout] claim 成功 - 再フェッチ:", {claimedHostessId: claimedId});
            const retry = await fetchHostessByUserId();
            data = retry.data;
            fetchError = retry.error ?? null;
          }
        }

        if (!data) {
          // ホステスレコードが見つからない場合 (救済後も空ならアウト)
          console.error("[ProtectedHostessLayout] ホステス権限なし - サインアウトしてリダイレクト");
          redirecting.current = true;
          await supabase.auth.signOut();
          clear();
          navigate("/sign-in?error=unauthorized", {replace: true});
          return;
        }

        // ホステスレコードが見つかった - グローバルストアに保存
        console.log("[ProtectedHostessLayout] ホステス権限確認完了:", {
          hostessId: data.id,
          hostessName: data.name,
        });
        setHostess(data);
      } catch (err) {
        console.error("[ProtectedHostessLayout] エラー発生:", err);
        setLoading(false);
      }
    }

    checkHostessPermission();
  }, [authLoading, session, user, navigate, supabase, hostess, setHostess, setLoading, clear]);

  // ユーザーが変わった場合はフラグをリセット
  useEffect(() => {
    fetchAttempted.current = false;
  }, [user?.id]);

  // ローディング中
  const isLoading = authLoading || hostessLoading;

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-stone-900">
        <div className="text-center">
          <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
          <p className="text-sm text-gray-400">読み込み中...</p>
        </div>
      </div>
    );
  }

  // 認証済み + ホステス権限あり → 子ルートを描画
  if (session && hostess) {
    return <Outlet />;
  }

  // 認証待ち（リダイレクト処理中）
  return null;
}
